import { useState, useEffect } from 'react';
import { Menu, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { SITE_CONFIG, IMAGES, scrollToSection } from '../lib/constants';

const navLinks = [
  { label: 'About', target: 'about' },
  { label: 'Our Process', target: 'process' },
  { label: 'Gallery', target: 'gallery' },
  { label: 'Contact', target: 'quote' },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNav = (target) => {
    scrollToSection(target);
    setOpen(false);
  };

  return (
    <>
      {/* Top Info Bar */}
      <div className="bg-[#0F1D32] text-white/80 text-xs py-2.5 hidden md:block">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href={`tel:${SITE_CONFIG.phone}`} className="flex items-center gap-2 hover:text-white transition-colors">
              <Phone className="h-3 w-3" />
              {SITE_CONFIG.phone}
            </a>
            <a href={`mailto:${SITE_CONFIG.email}`} className="flex items-center gap-2 hover:text-white transition-colors">
              <Mail className="h-3 w-3" />
              {SITE_CONFIG.email}
            </a>
          </div>
          <span className="text-white/50">{SITE_CONFIG.location}</span>
        </div>
      </div>

      {/* Main Navigation */}
      <header
        data-testid="header-nav"
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white shadow-md'
            : 'bg-white/5 backdrop-blur-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
          <a href="/" data-testid="header-logo" className="flex items-center">
            <img
              src={IMAGES.logo}
              alt="HDH Pools & Spas"
              className="h-12 md:h-14 rounded-lg"
            />
          </a>

          <nav className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <button
                key={link.target}
                onClick={() => handleNav(link.target)}
                data-testid={`nav-link-${link.target}`}
                className={`text-sm font-semibold tracking-wide uppercase transition-colors font-body ${
                  scrolled ? 'text-slate-700 hover:text-amber-600' : 'text-white/90 hover:text-amber-400'
                }`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              data-testid="header-client-login-btn"
              className={`text-xs uppercase tracking-wider font-semibold ${
                scrolled ? 'text-slate-600 hover:text-slate-900' : 'text-white/70 hover:text-white'
              }`}
              asChild
            >
              <a href={SITE_CONFIG.portalUrl}>Client Login</a>
            </Button>
            <Button
              data-testid="header-request-quote-btn"
              onClick={() => scrollToSection('quote')}
              className="bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs uppercase tracking-wider px-6 py-5 rounded-sm"
            >
              Get Free Quote
            </Button>
          </div>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button
                variant="ghost"
                size="icon"
                data-testid="mobile-menu-trigger"
                className={scrolled ? 'text-slate-800' : 'text-white'}
              >
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-[#0F1D32] border-none">
              <div className="flex flex-col gap-1 mt-12">
                {navLinks.map((link) => (
                  <button
                    key={link.target}
                    onClick={() => handleNav(link.target)}
                    className="text-left text-lg text-white/80 hover:text-amber-400 font-body py-3 border-b border-white/10 transition-colors"
                  >
                    {link.label}
                  </button>
                ))}
                <div className="mt-6 space-y-3">
                  <a href={`tel:${SITE_CONFIG.phone}`} className="flex items-center gap-3 text-sm text-white/60">
                    <Phone className="h-4 w-4" /> {SITE_CONFIG.phone}
                  </a>
                  <a href={`mailto:${SITE_CONFIG.email}`} className="flex items-center gap-3 text-sm text-white/60">
                    <Mail className="h-4 w-4" /> {SITE_CONFIG.email}
                  </a>
                </div>
                <Button
                  data-testid="mobile-request-quote-btn"
                  onClick={() => handleNav('quote')}
                  className="bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm uppercase tracking-wider mt-6 py-5 rounded-sm w-full"
                >
                  Get Free Quote
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>
    </>
  );
};
